import { IfpLoansAnalyticsProvider, useIfpAnalyticsService } from '@sec-loans-ui/ifp-loans/analytics/analyticsContext'
import { ApplicantInfoStep } from '@sec-loans-ui/ifp-loans/steps/applicant-info/ApplicantInfoStep'
import { CancelApplicationStep } from '@sec-loans-ui/ifp-loans/steps/cancel-application-step/CancelApplicationStep'
import { EndStep } from '@sec-loans-ui/ifp-loans/steps/end/EndStep'
import { FormContext } from '@sec-loans-ui/ifp-loans/steps/formContext'
import { GetApplicationStep } from '@sec-loans-ui/ifp-loans/steps/get-application/GetApplicationStep'
import { HouseholdInfoStep } from '@sec-loans-ui/ifp-loans/steps/household-info/HouseholdInfoStep'
import { IfpInfoStep } from '@sec-loans-ui/ifp-loans/steps/ifp-info/IfpInfoStep'
import { StatusStep } from '@sec-loans-ui/ifp-loans/steps/status/StatusStep'
import { AppId } from '@shared/types'
import { CommonWizardWrapper } from '@ui-common/components/CommonWizardWrapper'
import { useEffect } from 'react'

import ReturnApplicationStep from '../steps/return-application-step/ReturnApplicationStep'
import { SignAndSendStep } from '../steps/sign-and-send-step/SignAndSendStep'
import { useWizardLogic } from './useWizardLogic'
import { WizardContext, WizardStep } from './wizardMachine'

interface WizardProps {
  initialContext: Partial<WizardContext>
  onGoToApplicationsClick: () => void
  onNewApplicationClick: () => void
  onCancelledApplication: () => void
  onReturnedApplication: () => void
}

export const Wizard = (props: WizardProps) => {
  return (
    <IfpLoansAnalyticsProvider>
      <IfpLoansWizard {...props} />
    </IfpLoansAnalyticsProvider>
  )
}

const IfpLoansWizard = ({
  initialContext,
  onGoToApplicationsClick,
  onNewApplicationClick,
  onCancelledApplication,
  onReturnedApplication,
}: WizardProps) => {
  const { actor, ...formContext } = useWizardLogic(initialContext, onCancelledApplication, onReturnedApplication)
  const { showSubmitErrorModal, hasSubmitFailed } = formContext

  const { analyticsService } = useIfpAnalyticsService()
  const { step, exit, authToken, currentApplicantInfoIndex, showSendAbortPromptModal, applicationId } = formContext

  useEffect(() => {
    if (hasSubmitFailed) {
      showSubmitErrorModal()
    }
  }, [hasSubmitFailed, showSubmitErrorModal])

  const stepComponentMap: Partial<Record<WizardStep, () => React.ReactNode>> = {
    [WizardStep.GetApplicationStep]: () => (
      <GetApplicationStep parentService={actor} onGoBack={onGoToApplicationsClick} />
    ),
    [WizardStep.StatusStep]: () => (
      <StatusStep onGoToApplicationsClick={onGoToApplicationsClick} onNewApplicationClick={onNewApplicationClick} />
    ),
    [WizardStep.IfpInfoStep]: () => <IfpInfoStep />,
    [WizardStep.HouseholdInfoStep]: () => <HouseholdInfoStep />,
    [WizardStep.ApplicantInfoStep]: () => <ApplicantInfoStep key={currentApplicantInfoIndex} />,
    [WizardStep.SendStep]: () => (
      <SignAndSendStep applicationId={applicationId} onAbort={showSendAbortPromptModal} parentService={actor} />
    ),
    [WizardStep.ReturnApplicationStep]: () => (
      <ReturnApplicationStep parentService={actor} applicationId={applicationId} />
    ),
    [WizardStep.CancelApplicationStep]: () => (
      <CancelApplicationStep parentService={actor} applicationId={applicationId} />
    ),
    [WizardStep.EndStep]: () => (
      <EndStep onGoToApplicationsClick={onGoToApplicationsClick} onNewApplicationClick={onNewApplicationClick} />
    ),
  }

  return (
    <FormContext value={formContext}>
      <CommonWizardWrapper
        stepComponentMap={stepComponentMap}
        step={step}
        appId={AppId.SecLoans}
        analytics={{ analyticsService }}
        onLogout={exit}
        authToken={authToken ?? null}
        userContext={null}
      />
    </FormContext>
  )
}
